import type { SubjectLeftAction } from '@Application/Subjects/Actions/subjectLeft';
import type { ImportedDocumentsChangedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentsChanged';
import type { DocumentImportFailedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/documentImportFailed';
import type { DocumentImportedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/documentImported';
import type { ImportedDocumentRenameFailedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentRenameFailed';
import type { ImportedDocumentRenamedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentRenamed';
import type { ImportedDocumentsFetchFailedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentsFetchFailed';
import type { ImportedDocumentsFetchedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentsFetched';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ImportedDocumentView from '@UI/Store/ImportedDocuments/Views/ImportedDocumentView';
import ImportedDocumentsListView from '@UI/Store/ImportedDocuments/Views/ImportedDocumentsListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type ImportedDocumentsStore = {
	importedDocuments: ImportedDocumentsListView | null;
};

const initialState: ImportedDocumentsStore = {
	importedDocuments: null,
};

type Actions =
	| ImportedDocumentsFetchedAction
	| ImportedDocumentsChangedAction
	// Already carried by `useStoreQuery`'s own `error` field.
	| ImportedDocumentsFetchFailedAction
	| DocumentImportedAction
	| DocumentImportFailedAction
	| ImportedDocumentRenamedAction
	| ImportedDocumentRenameFailedAction
	| SubjectLeftAction
	| UserRevokedAction;

const whenImportedDocumentsFetched = (
	store: ImportedDocumentsStore,
	action: ImportedDocumentsFetchedAction,
): ImportedDocumentsStore => ({
	...store,
	importedDocuments: ImportedDocumentsListView.fromPayload(
		action.payload,
		action.typeKey,
		action.subjectId,
	),
});

// The event stream pushes the whole list of its dossier: it replaces the list held for that dossier, and
// a list that is absent or belongs to another dossier stays as it is.
const whenImportedDocumentsChanged = (
	store: ImportedDocumentsStore,
	action: ImportedDocumentsChangedAction,
): ImportedDocumentsStore => {
	if (!store.importedDocuments?.isFor(new SubjectAddress(action.typeKey, action.subjectId))) {
		return store;
	}

	return {
		...store,
		importedDocuments: ImportedDocumentsListView.fromPayload(
			action.payload,
			action.typeKey,
			action.subjectId,
		),
	};
};

// An import answered after the user moved to another dossier must not land in that dossier's list.
const whenDocumentImported = (
	store: ImportedDocumentsStore,
	action: DocumentImportedAction,
): ImportedDocumentsStore => {
	const list = store.importedDocuments;

	if (list === null || !list.isFor(new SubjectAddress(action.typeKey, action.subjectId))) {
		return store;
	}

	return {
		...store,
		importedDocuments: list.addImportedDocument(new ImportedDocumentView(action.payload)),
	};
};

// A rename answered after the user moved to another dossier must not touch that dossier's list.
const whenImportedDocumentRenamed = (
	store: ImportedDocumentsStore,
	action: ImportedDocumentRenamedAction,
): ImportedDocumentsStore => {
	const list = store.importedDocuments;

	if (list === null || !list.isFor(new SubjectAddress(action.typeKey, action.subjectId))) {
		return store;
	}

	return {
		...store,
		importedDocuments: list.replaceImportedDocument(new ImportedDocumentView(action.payload)),
	};
};

const identity = (store: ImportedDocumentsStore): ImportedDocumentsStore => store;

const whenSubjectLeft = (
	store: ImportedDocumentsStore,
	action: SubjectLeftAction,
): ImportedDocumentsStore => {
	const { typeKey, subjectId } = action.payload;

	if (!store.importedDocuments?.isFor(new SubjectAddress(typeKey, subjectId))) {
		return store;
	}

	return { ...store, importedDocuments: null };
};

const whenUserRevoked = (): ImportedDocumentsStore => initialState;

export default registerStore<'importedDocumentsStore', Actions>(
	'importedDocumentsStore',
	createSliceReducer({
		IMPORTED_DOCUMENTS_FETCHED: whenImportedDocumentsFetched,
		IMPORTED_DOCUMENTS_CHANGED: whenImportedDocumentsChanged,
		IMPORTED_DOCUMENTS_FETCH_FAILED: identity,
		DOCUMENT_IMPORTED: whenDocumentImported,
		DOCUMENT_IMPORT_FAILED: identity,
		IMPORTED_DOCUMENT_RENAMED: whenImportedDocumentRenamed,
		IMPORTED_DOCUMENT_RENAME_FAILED: identity,
		SUBJECT_LEFT: whenSubjectLeft,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
